<template>
  <div class="checkout-step-three">
    <div class="checkout-step__header">
      <div class="row align-items-end">
        <div class="col-12 col-md-9">
          <h3 class="checkout-step__headline">
            {{ $t('pages.checkoutStepThree.title') }}
          </h3>
          <p class="checkout-step__copy">
            {{ $t('pages.checkoutStepThree.copy') }}
          </p>
        </div>
        <div class="col-12 col-md-3">
          <small class="checkout-step__copy checkout-step__copy--small">
            {{ $t('generic.priceInfo') }}
          </small>
        </div>
      </div>
    </div>

    <div class="extra-selector">
      <div class="row">
        <div
          class="col-12 col-md-6 col-lg-12"
          v-for="extra in getAvailableExtras"
          :key="`${extra.name}-${extra.id}`"
        >
          <div class="extra">
            <CiSelectionBox
              :class="{ clickable: extra.extra_type !== 'free' }"
              :active="
                getExtraValue(extra) === true || getExtraValue(extra) > 0
              "
              @click.stop.native="
                extra.extra_type !== 'free' && onExtraClicked(extra, $event)
              "
            >
              <div slot="left" class="extra__status">
                <CiSwitch
                  :value="getExtraValue(extra)"
                  v-if="extra.extra_type !== 'free'"
                >
                </CiSwitch>
                <CiCounter
                  :value="getExtraValue(extra)"
                  :canIncrease="getExtraValue(extra) < extraMaxAmount"
                  :canDecrease="getExtraValue(extra) > extraMinAmount"
                  @update:value="onExtraAmountChange(extra, $event)"
                  v-if="extra.extra_type === 'free'"
                >
                </CiCounter>
              </div>
              <div slot="middle" class="extra__meta">
                <h3 class="meta__headline">{{ extra.name }}</h3>
                <div class="meta__description" v-html="extra.description"></div>
                <div class="d-flex align-items-center" 
                  v-if="isCampingCardAcsi(extra) && !extra.choice_full_stay && extra.duration_chosen > 0 && 
                    extra.duration !== extra.duration_chosen">
                  <div class="d-flex align-items-center max-content">
                    <iconCampingCard />
                  </div>
                  <p class="ml-1 mb-0">
                    <span v-if="extra.billing_type === 'per_day'">
                      {{ extra.duration_chosen === 1 ? $t('generic.labels.stayDay', {days: extra.duration_chosen}) : $t('generic.labels.stayDays', {days: extra.duration_chosen})}}
                    </span>
                    <span v-if="extra.billing_type === 'per_night'">
                      {{ extra.duration_chosen === 1 ? $t('generic.labels.stayNight', {days: extra.duration_chosen}) : $t('generic.labels.stayNights', {days: extra.duration_chosen})}}
                    </span>
                    <span class="text-lowercase">
                      {{ $t('generic.labels.included') }}
                    </span>
                  </p>
                </div>
              </div>
              <div slot="right" class="extra__price" >
                <div class="d-flex max-content">              
                  <iconCampingCard v-if="isCampingCardAcsi(extra)" />
                </div>
                <span
                  v-if="isCampingCardAcsi(extra) && extra.choice_full_stay"
                  class="price__meta price__meta--single"
                >
                  {{ $t('generic.labels.included') }}
                </span>
                <span
                  v-else-if="['included', 'per_consumption', 'on_site'].includes(extra.billing_type) && !isCampingCardAcsi(extra)"
                  class="price__meta price__meta--single"
                >
                  {{ $t(`generic.labels.${extra.billing_type}`) }}
                </span>
                <div v-else>
                  <div
                    v-if="extra.price_per_stay > 0"
                    class="mx-2 d-flex align-items-center flex-column"
                  >
                    <span class="price__value">
                      {{ extra.price_per_stay | currency(campground.currency, language.code) }}
                    </span>
                    <span
                      v-if="extra.currency_alt"
                      class="price__value price__value--alt"
                    >
                      {{ extra.currency_alt.price_per_stay | currency(currency.code, language.code) }}
                    </span>
                    <div class="price__meta">
                      {{ $t('generic.labels.perStay') }}
                    </div>
                  </div>
                  <div v-else>
                    <span class="price__meta price__meta--single">
                      {{ $t('generic.labels.included') }}
                    </span>
                  </div>
                </div>
                <div class="max-content">
                  <CiCampingCareAcsiInfo v-if="isCampingCardAcsi(extra)" 
                    :campground="campground" :currency="currency" :language="language"/>
                </div>
              </div>
            </CiSelectionBox>
          </div>
        </div>
        <div
          class="col-12 col-md-6 col-lg-12"
          v-for="extra in getMandatoryExtras"
          :key="extra.id"
        >
        <div class="extra">
            <CiSelectionBox
              :class="{ clickable: false }"
              :active="true"
              :disabled="true"
            >
              <div slot="left" class="extra__status">
                <span class="warning">
                  {{ $tc('generic.labels.mandatory_extra', 1) }}
                  <iconLock class="icon"></iconLock>
                </span>
              </div>
              <div slot="middle" class="extra__meta">
                <h3 class="meta__headline">{{ extra.name }}</h3>
                <div class="meta__description" v-html="extra.description"></div>
                <div class="d-flex align-items-center" 
                  v-if="isCampingCardAcsi(extra)
                    && (extra.duration_chosen > 0 || extra.persons_chosen > 0)
                    && (extra.duration !== extra.duration_chosen || extra.persons !== extra.persons_chosen)">
                  <div class="d-flex align-items-center max-content">
                    <iconCampingCard />
                  </div>
                  <p class="ml-1 mb-0">
                    <span v-if="extra.duration !== extra.duration_chosen">
                      <span v-if="extra.billing_type === 'per_day'">
                        {{ extra.duration_chosen === 1 ? $t('generic.labels.stayDay', {days: extra.duration_chosen}) : $t('generic.labels.stayDays', {days: extra.duration_chosen})}}
                      </span>
                      <span v-if="extra.billing_type === 'per_night'">
                        {{ extra.duration_chosen === 1 ? $t('generic.labels.stayNight', {days: extra.duration_chosen}) : $t('generic.labels.stayNights', {days: extra.duration_chosen})}}
                      </span>
                    </span>
                    <span v-if="extra.persons !== extra.persons_chosen">
                      {{ extra.persons_chosen }}
                      {{ $t(`generic.labels.persons`) }}
                    </span>
                    <span class="text-lowercase">
                      {{ $t('generic.labels.included') }}
                    </span>
                    {{ $t(`pages.checkoutStepThree.mandatoryExtras.${extra.billing_type}`) }}
                  </p>
                </div>
              </div>
              <div slot="right" class="extra__price" >
                <div class="d-flex max-content">
                  <iconCampingCard v-if="isCampingCardAcsi(extra)" />
                </div>
                <span class="price__meta price__meta--single" 
                    v-if="isCampingCardAcsi(extra) && extra.choice_full_stay 
                    && !(extra.duration !== extra.duration_chosen || extra.persons !== extra.persons_chosen)
                    && extra.price_per_stay === 0"> 
                    {{ $t(`generic.labels.included`) }}
                </span>
                <div class="mx-2 d-flex align-items-center flex-column" v-else>
                    <template v-if="extra.price_per_stay > 0">
                      <span class="price__value">
                        {{
                          extra.price_per_stay | currency(campground.currency, language.code)
                        }}</span
                      >
                      <span
                        class="price__value price__value--alt"
                        v-if="extra.currency_alt"
                        >{{
                          extra.currency_alt.price_per_stay
                            | currency(currency.code, language.code)
                        }}</span
                      >
                      <div class="price__meta">
                        {{ $t(`generic.labels.perStay`) }}
                      </div>
                    </template>
                    <div v-else-if="extra.billing_type === 'on_site'">
                       <span class="price__meta price__meta--single"> 
                        {{ $t(`generic.labels.${extra.billing_type}`) }}
                    </span>
                    </div>
                    <div v-else>
                      <span class="price__meta price__meta--single"> 
                        {{ $t(`generic.labels.included`) }}
                    </span>
                    </div>
                </div>
                <div class="max-content">
                  <CiCampingCareAcsiInfo v-if="isCampingCardAcsi(extra)" 
                    :campground="campground" :currency="currency" :language="language"/>
                </div>
              </div>
            </CiSelectionBox>
          </div>
        </div>
      </div>
    </div>

    <!-- <CiInfoBox
      :type="'warning'"
      v-if="
        getMandatoryExtrasPaidOnSite && getMandatoryExtrasPaidOnSite.length > 0
      "
    >

    {{  getMandatoryExtrasPaidOnSite }} {{ getMandatoryExtrasPaidOnSite.length > 0 }}
      <h4>{{ $t('pages.checkoutStepThree.infoBox.title') }}</h4>
      <p>{{ $t('pages.checkoutStepThree.infoBox.copy') }}</p>
      <div
        class="wrapper d-flex justify-content-between"
        v-for="mandatory in getMandatoryExtrasPaidOnSite"
        :key="mandatory.object_id"
      >
        <div class="label" >1 x {{ mandatory.name }} 
          <span>({{ $t(`pages.checkoutStepThree.mandatoryExtras.${mandatory.billing_type}`) }})</span></div>
        
      </div>
    </CiInfoBox> -->

    <CiProgressNavigation
      :active-step="activeStep"
      :status="status"
      :steps="steps"
    >
    </CiProgressNavigation>
  </div>
</template>

<script>
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import { mapGetters, mapActions } from 'vuex'
import {
  CiSelectionBox,
  CiInfoBox,
  CiCounter,
  CiSwitch,
  CiProgressNavigation,
  CiTooltip,
  CiCampingCareAcsiInfo,
} from '@/components/index'
import { DateTime } from 'luxon'
import { getJsDate } from '@/utils/date'
import iconLock from '@/assets/icons/lock-solid.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'
import iconCampingCard from '@/assets/icons/camping-card.svg'

export default {
  name: 'CheckoutStepThree',
  mixins: [bookingPriceMixin],
  components: {
    CiCounter,
    CiInfoBox,
    CiProgressNavigation,
    CiSelectionBox,
    CiSwitch,
    iconLock,
    CiTooltip,
    iconWarning,
    CiCampingCareAcsiInfo,
    iconCampingCard,
  },
  data() {
    return {
      extraMaxAmount: 99,
      extraMinAmount: 0,
    }
  },
  created() {
    this.trackEcommerceEvents(3)
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
      steps: 'steps',
    }),

    ...mapGetters('booking', {
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedExtras: 'selectedExtras',
      selectedSeasons: 'selectedSeasons',
      dates: 'dates',
      extraStock: 'extraStock',
    }),

    ...mapGetters('campground', {
      availableExtras: 'availableExtras',
      campground: 'campground',
      status: 'status',
      availableMandatoryExtras: 'availableMandatoryExtras',
    }),

    getAvailableExtras() {
      // first get basic extra data based on id of available extras
      const mappedExtras = this.availableExtras.map(extra => {
        const basicExtra = this.campground.extras.find(ex => ex.id === extra.id)
        const extraStock = this.extraStock
          ? this.extraStock.find(stock => stock.id === extra.id)
          : {}
        const extraSeasonDays = this.selectedCampsite.extra_season_days
          ? this.selectedCampsite.extra_season_days.filter(
              day => extra.id === parseInt(day.extra_id)
            )
          : []
        let extraPrice = { extra_cost_prices: [] }
        extraPrice.extra_cost_prices = extra.extra_cost_prices.map(price => {
          let priceData = extraSeasonDays.find(
            seasonDay => price.season === parseInt(seasonDay.season_id)
          )
          return { ...price, days: priceData && priceData.days }
        })
        extraPrice = extraPrice.extra_cost_prices.length > 0 ? extraPrice : {}
        return { ...basicExtra, ...extra, ...extraStock, ...extraPrice }
      })

      // filter extras which are not valid for selected campsite type
      return mappedExtras.filter(extra => {
        /* istanbul ignore else */
        if (this.selectedCampsite) {
          return extra[`applies_for_${this.selectedCampsite.campsite_type}`]
        }
      })
    },    
    numNights() {
      const d = this.dates
      if (!d || !d[1] || d[0] == null || d[1] == null) {
        return 0
      }

      const d1 = DateTime.fromJSDate(getJsDate(d[0]))
      const d2 = DateTime.fromJSDate(getJsDate(d[1]))

      return Math.round(Math.abs(d2.diff(d1, 'days').as('days')))
    },
    getMandatoryExtras() {
      //const filtredMandatoryExtras = this.availableMandatoryExtras.filter(x => x.billing_type !== 'included' && x.billing_type !== 'on_site')
      return this.availableMandatoryExtras
    },
    // getMandatoryExtrasPaidOnSite() {
    //   const filtredMandatoryExtras = this.availableMandatoryExtras.filter
    //     (x => (x.billing_type === 'included' || x.billing_type === 'on_site') && x.price_per_stay === 0)
    //   return filtredMandatoryExtras
    // } 
  },
  methods: {
    ...mapActions('application', {
      setStepStatus: 'setStepStatus',
      trackEcommerceEvents: 'trackEcommerceEvents',
    }),

    ...mapActions('booking', {
      setExtra: 'setExtra',
    }),

    onExtraAmountChange(extra, newAmount) {
      /* istanbul ignore else */
      if (newAmount >= this.extraMaxAmount) newAmount = this.extraMaxAmount
      /* istanbul ignore else */
      if (newAmount <= this.extraMinAmount) newAmount = this.extraMinAmount
      this.setExtra({ extra, amount: newAmount })
    },

    getExtraValue: function(extra) {
      const ext = this.selectedExtras.find(e => e.id === extra.id)
      return ext ? ext.amount : 0
    },

    onExtraClicked(extra, event) {
      // if actual target is a link, open href in new tab
      if (event.target.tagName.toLowerCase() === 'a') {
        window.open(event.target.href, '_blank')
        event.preventDefault()
      } else {
        this.setExtra({ extra, amount: !this.getExtraValue(extra) })
      }
    },
    isCampingCardAcsi(extra) {
      return extra && extra.choice === 'campingcard-acsi'
    },
  },
}
</script>

<style lang="scss" scoped>
.checkout-step-three__header {
  margin: 0 0 30px;
}

.checkout-step-three__headline {
  @include font-format(1.375rem, 1.625em);
}

.extra {
  margin: 0 0 20px;

  .warning {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1rem);

    position: relative;
    background-color: #fefcda;
    border-radius: 8px;
    color: #f5a623;
    padding: 8px 32px 6px 12px;
    text-transform: uppercase;

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10px, -50%);
      width: 11px;
      height: auto;
      fill: #f5a623;
    }
  }
}

.extra__reason {
  @include clearfix;
  @include font-format(0.75rem, 1.25rem);

  color: black;
  background-color: #fefcdd;
  padding: 5px 15px;
  margin: 0px;
  display: flex;
  align-items: center;

  @include media-breakpoint-up(lg) {
    margin: -25px;
    margin-top: 0px;
  }

  .icon {
    width: 20px;
    height: auto;
  }

  & > div {
    float: left;
  }

  .has-icon {
    width: 40px;
  }
  .reason-title {
    @include font('gotham_mediumregular');

    width: 150px;
  }
  .reason-content {
    width: 100%;
    margin-top: 10px;
    @include media-breakpoint-up(sm) {
      width: calc(100% - 190px);
      margin-top: 0;
    }

    .action {
      color: $primary-orange;
      text-decoration: underline;
      font-weight: bold;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

.extra__status {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;

  .warning {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1rem);

    position: relative;
    background-color: rgba(248, 231, 28, 0.15);
    border-radius: 8px;
    color: #f5a623;
    padding: 8px 32px 6px 12px;
    text-transform: uppercase;

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translate(-10px, -50%);
      width: 11px;
      height: auto;
      fill: #f5a623;
    }
  }
}

.extra__meta {
  border-bottom: 1px solid hsla(0, 0%, 60.8%, 0.5);
  flex: 3;
  padding: 20px;

  @include media-breakpoint-up(lg) {
    border: 0;
    padding: 0;
  }

  .meta__description {
    margin: 0;
    padding: 0;
  }
}

.free__text {
  font-size: 0.625rem;
  text-align: center;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
  color: #9b9b9b;
  font-weight: 400;
  font-family: gotham_mediumregular, Avenir, Helvetica, Arial, sans-serif;

  @include media-breakpoint-up(lg) {
    margin-top: 0.5rem;
    margin-bottom: 0rem;
    margin-left: 0rem;
  }
}

.extra__price {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  padding: 20px 0 20px 10px;

  @include media-breakpoint-up(lg) {
    align-items: center;
    padding: 0;
  }

  .price__value {
    @include font('gotham_mediumregular');
    @include font-format(1.375rem, 1em);

    &.price__value--alt {
      @include font-format(1rem, 1.25em);

      color: #9b9b9b;
      margin-top: 3px;
    }
    &.price--values {
      @include font-format(0.9rem, 1.25em);
    }
  }

  .price__meta {
    @include font('gotham_mediumregular');
    @include font-format(0.625rem, 1.625em);

    color: #9b9b9b;
    margin: 0 10px;

    &.price__meta--single {
      @include font-format(1rem, 1rem);

      color: black;
    }
  }

  .max-content {
    width: max-content
  }
}
</style>
